/**
 * Handles keyboard navigation within a collection of elements
 * @param {KeyboardEvent} event - The keyboard event
 * @param {number} currentIndex - The current focused index
 * @param {number} totalLength - Total number of items in the collection
 * @param {boolean} [circular=true] - Whether navigation should wrap around at the ends
 * @returns {number|null} The new index to focus, or null if no movement needed
 */
export function getNextFocusIndex(event, currentIndex, totalLength, circular = true) {
    if (totalLength <= 0) return null;

    let newIndex = currentIndex;
    
    switch(event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
            event.preventDefault();
            newIndex = currentIndex + 1;
            if (newIndex >= totalLength) {
                newIndex = circular ? 0 : totalLength - 1;
            }
            break;
            
        case 'ArrowLeft':
        case 'ArrowUp':
            event.preventDefault();
            newIndex = currentIndex - 1;
            if (newIndex < 0) {
                newIndex = circular ? totalLength - 1 : 0;
            }
            break;
            
        default:
            return null;
    }
    
    return newIndex === currentIndex ? null : newIndex;
}

/**
 * Handles calendar-specific keyboard navigation with grid-based movement
 * @param {KeyboardEvent} event - The keyboard event
 * @param {Element} focusedDate - Currently focused date element
 * @param {Element[]} allDates - Array of all active date elements
 * @param {Element} header - Header element to focus when shift+tab is pressed
 * @returns {Element|null} The next element to focus, or null if no movement needed
 */
export function getCalendarKeyboardAction(event, focusedDate, allDates, header) {
    if (!focusedDate) return null;

    const currentIndex = allDates.indexOf(focusedDate);
    const daysInWeek = 7;

    if (event.key === 'Tab') {
        if (event.shiftKey) {
            event.preventDefault();
            return header;
        }
        return null;
    }

    switch (event.key) {
        case 'ArrowLeft':
            if (currentIndex > 0) {
                return allDates[currentIndex - 1];
            }
            break;
        case 'ArrowRight':
            if (currentIndex < allDates.length - 1) {
                return allDates[currentIndex + 1];
            }
            break;
        case 'ArrowUp':
            if (currentIndex >= daysInWeek) {
                return allDates[currentIndex - daysInWeek];
            }
            break;
        case 'ArrowDown':
            if (currentIndex + daysInWeek < allDates.length) {
                return allDates[currentIndex + daysInWeek];
            }
            break;
    }

    return null;
}
